import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sidebar } from '../components/Sidebar';
import { configSandboxAPI } from '../utils/api';
import { toast } from 'sonner';
import {
  Plus, Play, Trash2, Loader2, Zap, Battery,
  Settings2, ArrowLeftRight, ChevronDown,
} from 'lucide-react';

const MODE_LABELS = {
  SOLAR_BILLS_DG_DISPLACEMENT: { label: 'Solar + Bills + DG', color: 'bg-violet-50 text-violet-700 border-violet-200' },
  BILLS_DG_DISPLACEMENT: { label: 'Bills + DG Displacement', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  BACKUP_SIZING: { label: 'Backup Sizing', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  SOLAR_SHIFTING: { label: 'Solar Shifting', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  BASIC_BESS_SIZING: { label: 'Basic BESS Sizing', color: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const SITE_TYPES = [
  { value: 'office', label: 'Office' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'hospital', label: 'Hospital' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'education', label: 'Education' },
  { value: 'retail_mall', label: 'Retail / Mall' },
  { value: 'custom', label: 'Custom' },
];

const DEFAULT_REQ = {
  use_case_backup: true,
  use_case_dg_replacement: false,
  use_case_peak_shaving: false,
  use_case_solar_shifting: false,
  critical_load_kw: 100,
  backup_hours_required: 3,
  has_solar: false,
  solar_pv_kwp: 0,
  solar_data_available: 'NONE',
  site_type: 'office',
  monthly_kwh: 0,
  max_demand_kw: 0,
  sanctioned_load_kw: 0,
  dg_runtime_hours: 0,
  dg_diesel_liters: 0,
};

function OptionCard({ opt }) {
  const confScore = opt.confidence_score || 0;
  const confColor = confScore >= 70 ? 'text-emerald-600' : confScore >= 40 ? 'text-amber-600' : 'text-red-500';
  const pricing = opt.indicative_pricing;

  return (
    <div className="border border-slate-100 rounded p-3 hover:border-indigo-200 transition-colors" data-testid={`opt-${opt.option_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2 text-xs">
          <span className="font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-700">{opt.option_name}</span>
          <span className="font-mono"><Zap className="w-3 h-3 inline mr-0.5" />{opt.pcs_power_kw} kW</span>
          <span className="font-mono"><Battery className="w-3 h-3 inline mr-0.5" />{opt.battery_energy_kwh} kWh</span>
          <span className="font-mono">{opt.recommended_backup_hours} hrs</span>
          <span className={`font-mono font-semibold ${confColor}`}>{confScore}%</span>
        </div>
      </div>
      <div className="flex items-center gap-2 mb-1 text-[10px]">
        <span className="bg-slate-50 px-2 py-0.5 rounded text-slate-600">{(opt.operating_mode || '').replace(/_/g, ' ')}</span>
        {opt.diesel_displaced_pct != null && <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded font-mono">~{opt.diesel_displaced_pct}% diesel</span>}
      </div>
      {opt.charging_rate_kw && (
        <div className="text-[10px] text-slate-500 bg-slate-50 rounded p-1.5 mb-1">
          Charging: <span className="font-mono">{opt.charging_rate_kw} kW</span> · <span className="font-mono">{opt.charging_window_hours} hrs</span>
          {opt.charging_assumptions && <div className="text-slate-400 mt-0.5">{opt.charging_assumptions}</div>}
        </div>
      )}
      {pricing && (
        <div className="text-[10px]">
          {pricing.pricing_complete ? (
            <span className="font-mono text-emerald-600">Est. ₹{(pricing.total_bess_cost_inr / 100000).toFixed(1)}L ({pricing.vendor})</span>
          ) : (
            <span className="text-amber-600">{pricing.message}</span>
          )}
        </div>
      )}
      {/* Calculation Debug Panel */}
      {(opt.pcs_sizing || opt.battery_sizing) && (
        <details className="text-[10px] text-slate-400 mt-1.5" data-testid={`debug-panel-${opt.option_id}`}>
          <summary className="cursor-pointer hover:text-slate-600 font-medium text-indigo-600">Debug</summary>
          <div className="mt-1 bg-indigo-50 border border-indigo-100 rounded p-2 space-y-1.5">
            {opt.pcs_sizing && (
              <div className="grid grid-cols-3 gap-1">
                <span>PCS: <span className="font-mono">{opt.pcs_sizing.pcs_unit_kw}kW x{opt.pcs_sizing.pcs_count} = {opt.pcs_sizing.total_pcs_kw}kW</span></span>
                <span>Oversize: <span className="font-mono">{opt.pcs_sizing.pcs_oversize_pct}%</span></span>
                {opt.pcs_sizing.pcs_vendor_name && <span>Vendor: {opt.pcs_sizing.pcs_vendor_name}</span>}
              </div>
            )}
            {opt.battery_sizing && (
              <div className="grid grid-cols-3 gap-1">
                <span>Battery: <span className="font-mono">{opt.battery_sizing.block_kwh}kWh x{opt.battery_sizing.block_count} = {opt.battery_sizing.total_battery_kwh}kWh</span></span>
                <span>Oversize: <span className="font-mono">{opt.battery_sizing.battery_oversize_pct}%</span></span>
                {opt.battery_sizing.battery_vendor_name && <span>Vendor: {opt.battery_sizing.battery_vendor_name}</span>}
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  );
}

function ScenarioPanel({ scenarios }) {
  if (!scenarios?.length) return null;

  return (
    <div className="bg-white border border-slate-200 rounded p-4" data-testid="compare-panel">
      <p className="text-xs font-bold text-slate-700 mb-3">Scenario Comparison</p>
      <div className="overflow-x-auto">
        <table className="w-full text-[10px]">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-2 py-1.5 text-left font-semibold">Scenario</th>
              <th className="px-2 py-1.5 text-center">Mode</th>
              <th className="px-2 py-1.5 text-center">Conf.</th>
              <th className="px-2 py-1.5 text-center">Option 1</th>
              <th className="px-2 py-1.5 text-center">Option 2</th>
              <th className="px-2 py-1.5 text-center">Option 3</th>
            </tr>
          </thead>
          <tbody>
            {scenarios.map((s, i) => (
              <tr key={s.name || i} className="border-t border-slate-100">
                <td className="px-2 py-1.5 font-medium">{s.name}</td>
                <td className="px-2 py-1.5 text-center">
                  <span className={`px-1.5 py-0.5 rounded border text-[9px] font-bold ${(MODE_LABELS[s.mode] || MODE_LABELS.BASIC_BESS_SIZING).color}`}>
                    {(MODE_LABELS[s.mode] || { label: s.mode }).label}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-center font-mono">{s.confidence_score}%</td>
                {(s.options || []).slice(0, 3).map((opt, j) => (
                  <td key={j} className="px-2 py-1.5 text-center">
                    <div className="font-mono">{opt.pcs_power_kw}kW / {opt.battery_energy_kwh}kWh</div>
                    <div className="text-slate-400">{opt.option_name}</div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ConfiguratorSandboxPage() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [req, setReq] = useState({ ...DEFAULT_REQ });
  const [title, setTitle] = useState('');
  const [latestRun, setLatestRun] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  const [scenarios, setScenarios] = useState([{ name: 'Scenario A', ...DEFAULT_REQ }, { name: 'Scenario B', ...DEFAULT_REQ }]);
  const [compareResult, setCompareResult] = useState(null);
  const [comparing, setComparing] = useState(false);

  const fetchRuns = useCallback(async () => {
    try {
      const res = await configSandboxAPI.listRuns();
      setRuns(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const handleRun = async () => {
    setRunning(true);
    try {
      const res = await configSandboxAPI.run({
        title: title || `Sandbox ${new Date().toLocaleString()}`,
        requirement: req,
      });
      setLatestRun(res.data);
      toast.success(`Mode: ${res.data.mode} | Confidence: ${res.data.confidence_score}%`);
      fetchRuns();
    } catch (e) { toast.error('Run failed'); }
    finally { setRunning(false); }
  };

  const handleCompare = async () => {
    setComparing(true);
    try {
      const res = await configSandboxAPI.compare(scenarios);
      setCompareResult(res.data.scenarios);
      toast.success(`${res.data.scenarios.length} scenarios compared`);
    } catch (e) { toast.error('Compare failed'); }
    finally { setComparing(false); }
  };

  const handleDeleteRun = async (runId) => {
    await configSandboxAPI.deleteRun(runId);
    toast.success('Deleted');
    if (latestRun?.sandbox_run_id === runId) setLatestRun(null);
    fetchRuns();
  };

  const updateScenario = (idx, field, value) => {
    setScenarios(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const setR = (k, v) => setReq(prev => ({ ...prev, [k]: v }));

  return (
    <div className="flex h-screen bg-slate-50" data-testid="config-sandbox-page">
      <Sidebar />
      <div className="flex-1 ml-64 flex overflow-hidden">
        {/* Left: Run History Panel — matches Rental Sandbox layout */}
        <div className="w-64 bg-white border-r border-slate-200 flex flex-col">
          <div className="p-3 border-b border-slate-100 flex items-center justify-between">
            <p className="text-xs font-bold text-slate-700">Run History</p>
            <Button size="sm" variant={showCompare ? 'default' : 'outline'} className="text-[10px] h-7"
              onClick={() => setShowCompare(!showCompare)} data-testid="toggle-compare">
              <ArrowLeftRight className="w-3 h-3 mr-0.5" /> Compare
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? <p className="text-xs text-slate-400 p-3">Loading...</p> : runs.length === 0 ? (
              <div className="p-4 text-center text-xs text-slate-400">No runs yet. Run a config to start.</div>
            ) : runs.map(run => (
              <button key={run.sandbox_run_id}
                onClick={() => setLatestRun(run)}
                className={`w-full text-left px-3 py-2.5 border-b border-slate-50 hover:bg-slate-50 transition-colors ${latestRun?.sandbox_run_id === run.sandbox_run_id ? 'bg-indigo-50 border-l-2 border-l-indigo-500' : ''}`}
                data-testid={`history-${run.sandbox_run_id}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-slate-700 truncate">{run.title}</p>
                  <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-300 hover:text-red-500" onClick={e => { e.stopPropagation(); handleDeleteRun(run.sandbox_run_id); }}>
                    <Trash2 className="w-2.5 h-2.5" />
                  </Button>
                </div>
                <div className="flex items-center space-x-2 mt-1 text-[9px]">
                  <span className={`px-1 py-0.5 rounded border ${(MODE_LABELS[run.mode] || MODE_LABELS.BASIC_BESS_SIZING).color}`}>
                    {(MODE_LABELS[run.mode] || { label: '?' }).label}
                  </span>
                  <span className="font-mono text-slate-500">{run.confidence_score}%</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Header */}
          <div>
            <h1 className="text-lg font-bold text-slate-800">Configurator Sandbox</h1>
            <p className="text-xs text-slate-400">Run BESS sizing scenarios independently from the prospect workflow</p>
          </div>

          {!showCompare ? (
            <div className="grid grid-cols-12 gap-4">
              {/* Inputs */}
              <div className="col-span-4 bg-white border border-slate-200 rounded p-4 space-y-3">
                <p className="text-xs font-bold text-slate-700">Requirement Inputs</p>
                <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Run title" className="text-xs h-8" data-testid="sandbox-title" />

                {/* Use Case Flags */}
                <div>
                  <Label className="text-[10px] font-semibold text-slate-500">Use Case Flags</Label>
                  <div className="grid grid-cols-2 gap-1 mt-1">
                    {[
                      { key: 'use_case_backup', label: 'Backup' },
                      { key: 'use_case_dg_replacement', label: 'DG Replace' },
                      { key: 'use_case_peak_shaving', label: 'Peak Shave' },
                      { key: 'use_case_solar_shifting', label: 'Solar Shift' },
                    ].map(uc => (
                      <label key={uc.key} className="flex items-center space-x-1.5 text-[10px] cursor-pointer">
                        <input type="checkbox" checked={req[uc.key]} onChange={e => setR(uc.key, e.target.checked)} className="rounded" data-testid={`sb-${uc.key}`} />
                        <span>{uc.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Site Type */}
                <div>
                  <Label className="text-[10px] text-slate-500">Site Type</Label>
                  <select value={req.site_type} onChange={e => setR('site_type', e.target.value)}
                    className="w-full mt-0.5 h-8 text-xs border border-slate-200 rounded px-2" data-testid="sb-site-type">
                    {SITE_TYPES.map(st => <option key={st.value} value={st.value}>{st.label}</option>)}
                  </select>
                </div>

                {/* Load */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] text-slate-500">Critical Load (kW)</Label>
                    <Input type="number" value={req.critical_load_kw} onChange={e => setR('critical_load_kw', parseFloat(e.target.value) || 0)} className="h-8 text-xs font-mono mt-0.5" data-testid="sb-critical-load" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">Backup Hours</Label>
                    <Input type="number" step="0.5" value={req.backup_hours_required} onChange={e => setR('backup_hours_required', parseFloat(e.target.value) || 0)} className="h-8 text-xs font-mono mt-0.5" data-testid="sb-backup-hours" />
                  </div>
                </div>

                {/* Bills proxy data */}
                <div className="border-t border-slate-100 pt-2">
                  <Label className="text-[10px] font-semibold text-slate-500">Bill Data (optional)</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <div>
                      <Label className="text-[10px] text-slate-400">Monthly kWh</Label>
                      <Input type="number" value={req.monthly_kwh} onChange={e => setR('monthly_kwh', parseFloat(e.target.value) || 0)} className="h-8 text-xs font-mono mt-0.5" data-testid="sb-monthly-kwh" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-slate-400">Max Demand (kW)</Label>
                      <Input type="number" value={req.max_demand_kw} onChange={e => setR('max_demand_kw', parseFloat(e.target.value) || 0)} className="h-8 text-xs font-mono mt-0.5" data-testid="sb-max-demand" />
                    </div>
                  </div>
                </div>

                {/* DG proxy */}
                <div className="border-t border-slate-100 pt-2">
                  <Label className="text-[10px] font-semibold text-slate-500">DG Data (optional)</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <div>
                      <Label className="text-[10px] text-slate-400">Runtime hrs/mo</Label>
                      <Input type="number" value={req.dg_runtime_hours} onChange={e => setR('dg_runtime_hours', parseFloat(e.target.value) || 0)} className="h-8 text-xs font-mono mt-0.5" data-testid="sb-dg-runtime" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-slate-400">Diesel L/mo</Label>
                      <Input type="number" value={req.dg_diesel_liters} onChange={e => setR('dg_diesel_liters', parseFloat(e.target.value) || 0)} className="h-8 text-xs font-mono mt-0.5" data-testid="sb-dg-diesel" />
                    </div>
                  </div>
                </div>

                {/* Solar */}
                <div className="border-t border-slate-100 pt-2">
                  <label className="flex items-center space-x-1.5 text-[10px] cursor-pointer">
                    <input type="checkbox" checked={req.has_solar} onChange={e => setR('has_solar', e.target.checked)} className="rounded" data-testid="sb-has-solar" />
                    <span className="font-semibold text-slate-500">Solar Available</span>
                  </label>
                  {req.has_solar && (
                    <div className="mt-1">
                      <Label className="text-[10px] text-slate-400">PV kWp</Label>
                      <Input type="number" value={req.solar_pv_kwp} onChange={e => setR('solar_pv_kwp', parseFloat(e.target.value) || 0)} className="h-8 text-xs font-mono mt-0.5" data-testid="sb-solar-kwp" />
                    </div>
                  )}
                </div>

                <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-xs" onClick={handleRun} disabled={running} data-testid="sb-run-btn">
                  {running ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                  Run Configurator
                </Button>
              </div>

              {/* Results */}
              <div className="col-span-8 space-y-3">
                {latestRun ? (
                  <>
                    <div className={`border rounded p-3 flex items-center justify-between ${(MODE_LABELS[latestRun.mode] || MODE_LABELS.BASIC_BESS_SIZING).color}`} data-testid="sb-mode-banner">
                      <div>
                        <p className="text-xs font-bold">Mode: {(MODE_LABELS[latestRun.mode] || { label: latestRun.mode }).label}</p>
                        <p className="text-[10px] opacity-80">{latestRun.mode_reason}</p>
                      </div>
                      <span className="font-mono text-xs font-bold">{latestRun.confidence_score}%</span>
                    </div>

                    {/* Missing inputs */}
                    {latestRun.missing_inputs?.some(i => i.status !== 'ok') && (
                      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-[10px]" data-testid="sb-missing-inputs">
                        <p className="font-semibold text-amber-800 mb-1">Missing inputs</p>
                        {latestRun.missing_inputs.filter(i => i.status !== 'ok').map((item, i) => (
                          <p key={item.input || i} className="text-amber-700">{item.status === 'partial' ? '⚠' : '✗'} {item.input}</p>
                        ))}
                      </div>
                    )}

                    {/* Options */}
                    <div className="space-y-2">
                      {(latestRun.options || []).map(opt => <OptionCard key={opt.option_id} opt={opt} />)}
                    </div>
                  </>
                ) : (
                  <div className="bg-white border border-slate-200 rounded p-8 text-center">
                    <Settings2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">Configure inputs and run</p>
                    <p className="text-xs text-slate-400 mt-1">Results will appear here</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Compare Mode */
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 rounded p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold text-slate-700">Compare Scenarios</p>
                  <div className="flex space-x-2">
                    <Button size="sm" variant="outline" className="text-[10px] h-7" onClick={() => setScenarios(prev => [...prev, { name: `Scenario ${String.fromCharCode(65 + prev.length)}`, ...DEFAULT_REQ }])} data-testid="add-compare-scenario">
                      <Plus className="w-3 h-3 mr-0.5" /> Add
                    </Button>
                    <Button size="sm" className="bg-indigo-600 text-[10px] h-7" onClick={handleCompare} disabled={comparing} data-testid="run-compare-btn">
                      {comparing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3 mr-0.5" />}
                      Compare
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {scenarios.map((s, idx) => (
                    <div key={idx} className="border border-slate-100 rounded p-3 space-y-2" data-testid={`compare-scenario-${idx}`}>
                      <div className="flex items-center justify-between">
                        <Input value={s.name} onChange={e => updateScenario(idx, 'name', e.target.value)} className="h-7 text-xs font-medium border-0 px-0 shadow-none" />
                        {scenarios.length > 2 && (
                          <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-400" onClick={() => setScenarios(prev => prev.filter((_, i) => i !== idx))}>
                            <Trash2 className="w-2.5 h-2.5" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-1 text-[10px]">
                        {[
                          { key: 'use_case_backup', label: 'Backup' },
                          { key: 'use_case_dg_replacement', label: 'DG Replace' },
                        ].map(uc => (
                          <label key={uc.key} className="flex items-center space-x-1 cursor-pointer">
                            <input type="checkbox" checked={s[uc.key]} onChange={e => updateScenario(idx, uc.key, e.target.checked)} className="rounded w-3 h-3" />
                            <span>{uc.label}</span>
                          </label>
                        ))}
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        <div>
                          <Label className="text-[9px] text-slate-400">Load kW</Label>
                          <Input type="number" value={s.critical_load_kw} onChange={e => updateScenario(idx, 'critical_load_kw', parseFloat(e.target.value) || 0)} className="h-6 text-[10px] font-mono" />
                        </div>
                        <div>
                          <Label className="text-[9px] text-slate-400">Backup hrs</Label>
                          <Input type="number" step="0.5" value={s.backup_hours_required} onChange={e => updateScenario(idx, 'backup_hours_required', parseFloat(e.target.value) || 0)} className="h-6 text-[10px] font-mono" />
                        </div>
                        <div>
                          <Label className="text-[9px] text-slate-400">kWh/mo</Label>
                          <Input type="number" value={s.monthly_kwh || 0} onChange={e => updateScenario(idx, 'monthly_kwh', parseFloat(e.target.value) || 0)} className="h-6 text-[10px] font-mono" />
                        </div>
                        <div>
                          <Label className="text-[9px] text-slate-400">Site</Label>
                          <select value={s.site_type} onChange={e => updateScenario(idx, 'site_type', e.target.value)} className="w-full h-6 text-[10px] border border-slate-200 rounded px-1">
                            {SITE_TYPES.map(st => <option key={st.value} value={st.value}>{st.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <label className="flex items-center space-x-1 text-[10px] cursor-pointer">
                        <input type="checkbox" checked={s.has_solar} onChange={e => updateScenario(idx, 'has_solar', e.target.checked)} className="rounded w-3 h-3" />
                        <span>Solar</span>
                      </label>
                      {s.has_solar && (
                        <div>
                          <Label className="text-[9px] text-slate-400">PV kWp</Label>
                          <Input type="number" value={s.solar_pv_kwp || 0} onChange={e => updateScenario(idx, 'solar_pv_kwp', parseFloat(e.target.value) || 0)} className="h-6 text-[10px] font-mono" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {compareResult && <ScenarioPanel scenarios={compareResult} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
